import React, {useEffect, useRef, useState} from "react";
import {Box, Button, ButtonProps, CircularProgress, DialogActions, DialogContent, TextField, Typography} from "@mui/material";
import {ConfirmationResult} from "firebase/auth";
import {closeAccountCopy, reauthErrorText, reauthMethod, ReauthMethod} from "./account-closure.service";
import {
  AccountIdentity,
  clearPhoneRecaptcha,
  confirmPhoneReauth,
  currentAccountIdentity,
  currentSignInMethod,
  reauthenticateWithGoogle,
  reauthenticateWithPassword,
  startPhoneReauth,
} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {displayPhone} from "../../shared/phone";

const ErrorText = ({text}: {text: string | null}) => text
  ? <Typography variant="body2" color="error" sx={{mt: 1}}>{text}</Typography>
  : null;

interface ConfirmItsYouProps {
  /** Runs once the person has signed in again; the next token carries the new sign-in time. */
  onConfirmed: () => void;
  onBack: () => void;
  /** Shown under the field on arrival, such as the server asking again. */
  initialError?: string | null;
  /** Told while a sign-in is out, so the dialog is not closed under it. */
  onBusyChange?: (busy: boolean) => void;
  recaptchaId: string;
  /** The confirming buttons' color: "error" where the step leads to deleting the account. */
  confirmColor?: ButtonProps['color'];
}

// "Confirm it's you", a dialog step: a fresh sign-in with the method the
// session used (or else the first linked one this app can run), which the
// server asks for before closing or restoring an account. Remount it (a new
// key) to start over with an empty field.
export const ConfirmItsYou = ({onConfirmed, onBack, initialError = null, onBusyChange, recaptchaId, confirmColor = 'primary'}: ConfirmItsYouProps) => {
  const [identity] = useState<AccountIdentity>(currentAccountIdentity);
  const [method, setMethod] = useState<ReauthMethod | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(initialError);
  const [busy, setBusy] = useState(false);
  // The phone step's reCAPTCHA is the one the sign-in page also uses, so
  // this step clears it only when it made one.
  const recaptchaInUse = useRef(false);

  useEffect(() => {
    let cancelled = false;
    currentSignInMethod()
      .catch(() => null)
      .then(provider => {
        if (!cancelled) {
          setMethod(reauthMethod(provider, identity.linkedProviders));
        }
      });
    return () => {
      cancelled = true;
      if (recaptchaInUse.current) {
        clearPhoneRecaptcha();
        recaptchaInUse.current = false;
      }
    };
  }, [identity]);

  const working = (value: boolean) => {
    setBusy(value);
    onBusyChange?.(value);
  };

  const failed = (e: unknown) => {
    const text = reauthErrorText(e);
    if (text === closeAccountCopy.genericError) {
      logError("Confirm it's you failed", {}, e as Error);
    }
    setError(text);
  };

  const confirm = async (attempt: () => Promise<void>) => {
    if (busy) {
      return;
    }
    working(true);
    setError(null);
    try {
      await attempt();
    } catch (e) {
      failed(e);
      return;
    } finally {
      working(false);
    }
    onConfirmed();
  };

  const sendCode = async () => {
    if (busy) {
      return;
    }
    working(true);
    setError(null);
    try {
      recaptchaInUse.current = true;
      setConfirmation(await startPhoneReauth(recaptchaId));
      setCode('');
    } catch (e) {
      failed(e);
    } finally {
      working(false);
    }
  };

  const back = <Button onClick={onBack} disabled={busy} color="inherit">Back</Button>;

  switch (method) {
    case null:
      return (
        <DialogContent>
          <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}><CircularProgress size={28}/></Box>
        </DialogContent>
      );
    case 'password':
      return (
        <>
          <DialogContent>
            <Typography variant="body1">Enter the password for {identity.email}.</Typography>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              disabled={busy}
              error={error !== null}
              helperText={error}
              autoFocus
              fullWidth
              margin="normal"
              autoComplete="current-password"
              onKeyDown={event => {
                if (event.key === 'Enter' && password) {
                  void confirm(() => reauthenticateWithPassword(password));
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>
            {back}
            <Button
              variant="contained"
              color={confirmColor}
              onClick={() => confirm(() => reauthenticateWithPassword(password))}
              disabled={!password || busy}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      );
    case 'phone':
      return (
        <>
          <DialogContent>
            <Typography variant="body1">
              We'll text a code to {identity.phoneNumber ? displayPhone(identity.phoneNumber) : ''}.
            </Typography>
            {confirmation ? (
              <TextField
                label="Code"
                value={code}
                onChange={event => setCode(event.target.value)}
                disabled={busy}
                error={error !== null}
                helperText={error}
                autoFocus
                fullWidth
                margin="normal"
                autoComplete="one-time-code"
                inputProps={{inputMode: 'numeric'}}
                onKeyDown={event => {
                  if (event.key === 'Enter' && code.trim()) {
                    void confirm(() => confirmPhoneReauth(confirmation, code.trim()));
                  }
                }}
              />
            ) : (
              <ErrorText text={error}/>
            )}
            <Box id={recaptchaId}/>
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>
            {back}
            {confirmation ? (
              <Button
                variant="contained"
                color={confirmColor}
                onClick={() => confirm(() => confirmPhoneReauth(confirmation, code.trim()))}
                disabled={!code.trim() || busy}
              >
                Continue
              </Button>
            ) : (
              <Button variant="contained" color={confirmColor} onClick={sendCode} disabled={busy}>Send code</Button>
            )}
          </DialogActions>
        </>
      );
    case 'google':
      return (
        <>
          <DialogContent>
            <Typography variant="body1">Sign in with Google again as {identity.email}.</Typography>
            <ErrorText text={error}/>
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>
            {back}
            <Button variant="contained" color={confirmColor} onClick={() => confirm(reauthenticateWithGoogle)} disabled={busy}>
              Continue with Google
            </Button>
          </DialogActions>
        </>
      );
    case 'unavailable':
      return (
        <>
          <DialogContent>
            <Typography variant="body1">{closeAccountCopy.reauthUnavailable}</Typography>
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>{back}</DialogActions>
        </>
      );
  }
};
